import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  createNotification,
  getAllNotifications,
  deleteNotification,
} from "../../firebase";
import { Notification } from "../../types";
import { format } from "date-fns";
import {
  BellIcon,
  PaperAirplaneIcon,
  TrashIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

// Format date helper
const formatDate = (timestamp: number) => {
  return format(new Date(timestamp), "MMM d, yyyy h:mm a");
};

const NotificationManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState<
    string | null
  >(null);
  const [alert, setAlert] = useState<{
    show: boolean;
    message: string;
    type: "success" | "error";
  }>({
    show: false,
    message: "",
    type: "success",
  });

  // Load notifications when component mounts
  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoadingNotifications(true);
      const result = await getAllNotifications();
      setNotifications(result);
    } catch (error: any) {
      console.error("Error loading notifications:", error);
      setAlert({
        show: true,
        message: `Failed to load notifications: ${error.message}`,
        type: "error",
      });
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleTabChange = (tabIndex: number) => {
    setTabValue(tabIndex);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !message.trim()) {
      setAlert({
        show: true,
        message: "Please enter both title and message",
        type: "error",
      });
      return;
    }

    try {
      setLoading(true);
      await createNotification(
        title.trim(),
        message.trim(),
        currentUser?.uid || "unknown"
      );

      // Reset form
      setTitle("");
      setMessage("");

      // Refresh notifications list
      fetchNotifications();

      // Show success message
      setAlert({
        show: true,
        message: "Notification sent successfully!",
        type: "success",
      });
    } catch (error: any) {
      console.error("Error sending notification:", error);
      setAlert({
        show: true,
        message: `Failed to send notification: ${error.message}`,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const closeAlert = () => {
    setAlert({ ...alert, show: false });
  };

  const openDeleteDialog = (notificationId: string) => {
    setNotificationToDelete(notificationId);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setNotificationToDelete(null);
  };

  const handleDeleteNotification = async () => {
    if (!notificationToDelete) return;

    try {
      await deleteNotification(notificationToDelete);

      // Update local state to reflect deletion
      setNotifications((prevNotifications) =>
        prevNotifications.filter((n) => n.id !== notificationToDelete)
      );

      setAlert({
        show: true,
        message: "Notification deleted successfully",
        type: "success",
      });
    } catch (error: any) {
      console.error("Error deleting notification:", error);
      setAlert({
        show: true,
        message: `Failed to delete notification: ${error.message}`,
        type: "error",
      });
    } finally {
      closeDeleteDialog();
    }
  };

  return (
    <div className="bg-white rounded-lg p-6">
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => handleTabChange(0)}
            className={`${
              tabValue === 0
                ? "border-gold text-gold"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Create Notification
          </button>
          <button
            onClick={() => handleTabChange(1)}
            className={`${
              tabValue === 1
                ? "border-gold text-gold"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Notification History
          </button>
        </nav>
      </div>

      {/* Create Notification Tab */}
      <div className={tabValue === 0 ? "block" : "hidden"}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">
            Send Notification
          </h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="notification-title"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Notification Title
            </label>
            <input
              id="notification-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gold focus:border-gold"
              required
              disabled={loading}
            />
          </div>

          <div className="mb-6">
            <label
              htmlFor="notification-message"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Notification Message
            </label>
            <textarea
              id="notification-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gold focus:border-gold"
              required
              disabled={loading}
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold ${
                loading ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {loading ? (
                <>
                  <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-5 w-5" />
                  Sending...
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="-ml-1 mr-2 h-5 w-5" />
                  Send Notification
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Notification History Tab */}
      <div className={tabValue === 1 ? "block" : "hidden"}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">
            Notification History
          </h2>
          <button
            onClick={fetchNotifications}
            disabled={loadingNotifications}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold"
          >
            <ArrowPathIcon
              className={`-ml-0.5 mr-2 h-4 w-4 ${
                loadingNotifications ? "animate-spin" : ""
              }`}
            />
            Refresh
          </button>
        </div>

        {loadingNotifications ? (
          <div className="flex justify-center py-12">
            <ArrowPathIcon className="h-8 w-8 text-gold animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-6 text-center">
            <BellIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No notifications
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              No notifications have been sent yet.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Date
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Title
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Message
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {notifications.map((notification) => (
                  <tr key={notification.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(notification.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {notification.title}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {notification.message.length > 100
                        ? `${notification.message.substring(0, 100)}...`
                        : notification.message}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openDeleteDialog(notification.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg overflow-hidden shadow-xl max-w-md w-full">
            <div className="px-6 py-4">
              <div className="text-lg font-medium text-gray-900 mb-2">
                Delete Notification
              </div>
              <p className="text-sm text-gray-500">
                Are you sure you want to delete this notification? This action
                cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={closeDeleteDialog}
                className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteNotification}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <TrashIcon className="-ml-1 mr-2 h-5 w-5" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert/Notification */}
      {alert.show && (
        <div className="fixed bottom-4 right-4 max-w-md w-full">
          <div
            className={`rounded-md p-4 shadow-lg ${
              alert.type === "success" ? "bg-green-50" : "bg-red-50"
            }`}
          >
            <div className="flex">
              <div className="flex-shrink-0">
                {alert.type === "success" ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-400" />
                ) : (
                  <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
                )}
              </div>
              <div className="ml-3">
                <p
                  className={`text-sm font-medium ${
                    alert.type === "success" ? "text-green-800" : "text-red-800"
                  }`}
                >
                  {alert.message}
                </p>
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button
                    onClick={closeAlert}
                    className={`inline-flex rounded-md p-1.5 ${
                      alert.type === "success"
                        ? "bg-green-50 text-green-500 hover:bg-green-100"
                        : "bg-red-50 text-red-500 hover:bg-red-100"
                    }`}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationManagement;
